(() => {
  'use strict';
  let cleanup = () => {};
  function mount() {
    cleanup();
    const root = document.getElementById('pink-dusk-home');
    if (!root) return;
    const config = root.querySelector('#pd-config');
    if (!config) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const on = (el, name, callback, options = {}) => el?.addEventListener(name, callback, { ...options, signal });
    const find = selector => root.querySelector(selector);
    const icon = name => config.getAttribute('data-icon-' + name);
    const label = name => config.getAttribute('data-label-' + name);
    const films = [...root.querySelectorAll('[data-film]')];
    // Keep offscreen artwork out of the first-screen download queue.
    const revealMedia = element => {
      element.classList.add('pd-media-ready');
      element.querySelectorAll('video[data-poster]').forEach(video => {
        video.poster = video.dataset.poster;
        delete video.dataset.poster;
      });
    };
    const mediaObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      revealMedia(entry.target);
      mediaObserver.unobserve(entry.target);
    }), { rootMargin: '400px 0px' });
    root.querySelectorAll('section').forEach(section => mediaObserver.observe(section));
    const hero = root.querySelector('.pd-hero-art');
    let heroReady = !hero;
    let heroPoster;
    function startFilmsAfterPoster() {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (signal.aborted) return;
        heroReady = true;
        syncFilms();
      }));
    }
    if (hero) {
      heroPoster = new Image();
      heroPoster.onload = heroPoster.onerror = startFilmsAfterPoster;
      heroPoster.src = hero.poster;
    }

    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const motionButton = find('.pd-motion-toggle');
    let motionPaused = reducedMotion.matches;
    const visibility = new Map();
    const manualPaused = new Set();
    function updateFilmButton(video) {
      const wrapper = video.closest('.pd-film');
      const button = wrapper?.querySelector('.pd-film-toggle');
      if (!button) return;
      wrapper.toggleAttribute('data-paused', video.paused);
      button.setAttribute('aria-label', `${label(video.paused ? 'play' : 'pause')} ${video.getAttribute('aria-label')}`);
      button.querySelector('img').src = icon(video.paused ? 'play' : 'pause');
    }
    const play = video => video.play().catch(() => updateFilmButton(video));
    function syncFilms() {
      films.forEach(video => {
        if (heroReady && !document.hidden && !motionPaused && visibility.get(video) && !manualPaused.has(video)) play(video);
        else video.pause();
      });
    }
    const filmObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => visibility.set(entry.target, entry.isIntersecting));
      syncFilms();
    }, { threshold: 0.12 });
    films.forEach(video => {
      video.muted = true;
      if (motionPaused) { video.autoplay = false; video.pause(); }
      filmObserver.observe(video);
      on(video, 'play', () => updateFilmButton(video));
      on(video, 'pause', () => updateFilmButton(video));
      on(video.closest('.pd-film')?.querySelector('.pd-film-toggle'), 'click', () => {
        if (video.paused) { manualPaused.delete(video); play(video); }
        else { manualPaused.add(video); video.pause(); }
      });
      updateFilmButton(video);
    });
    function syncMotion() {
      root.classList.toggle('pd-motion-paused', motionPaused);
      if (motionButton) {
        motionButton.setAttribute('aria-pressed', String(motionPaused));
        motionButton.setAttribute('aria-label', label(motionPaused ? 'play-animations' : 'pause-animations'));
        motionButton.title = motionButton.getAttribute('aria-label');
        motionButton.querySelector('img').src = icon(motionPaused ? 'play' : 'pause');
      }
      syncFilms();
    }
    on(motionButton, 'click', () => { motionPaused = !motionPaused; syncMotion(); });
    on(reducedMotion, 'change', () => { motionPaused = reducedMotion.matches; syncMotion(); });
    syncMotion();
    const sceneObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      entry.target.classList.toggle('pd-is-inview', entry.isIntersecting);
      if (entry.isIntersecting && entry.target.id === 'pd-dress' && !reducedMotion.matches) {
        entry.target.querySelector('.pd-bird-flight')?.classList.add('pd-flying');
      }
    }), { threshold: 0.12 });
    root.querySelectorAll('.pd-theatre,.pd-letters,.pd-dress-intro').forEach(el => sceneObserver.observe(el));
    on(find('[data-card]'), 'click', event => {
      event.currentTarget.setAttribute('aria-pressed', String(event.currentTarget.classList.toggle('pd-is-flipped')));
    });
    const backTop = find('.pd-back-top');
    const topObserver = new IntersectionObserver(entries => backTop?.classList.toggle('pd-visible', !entries[0].isIntersecting), { threshold: 0.1 });
    if (find('.pd-hero')) topObserver.observe(find('.pd-hero'));
    const openers = new WeakMap();
    function openDialog(dialog) {
      if (!dialog) return;
      root.querySelectorAll('dialog[open]').forEach(other => other.close());
      openers.set(dialog, document.activeElement);
      dialog.showModal();
      document.body.classList.add('pd-locked');
      dialog.querySelector('.pd-dialog-close')?.focus();
    }
    root.querySelectorAll('dialog').forEach(dialog => {
      on(dialog.querySelector('.pd-dialog-close'), 'click', () => dialog.close());
      on(dialog, 'click', event => {
        const b = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < b.left || event.clientX > b.right || event.clientY < b.top || event.clientY > b.bottom)) dialog.close();
      });
      on(dialog, 'close', () => {
        if (root.querySelector('dialog[open]')) return;
        document.body.classList.remove('pd-locked');
        const opener = openers.get(dialog);
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      });
    });
    const looks = [...root.querySelectorAll('[data-look]')];
    const lookDialog = find('#pd-look-dialog');
    let lookIndex = 0;
    function showLook(index) {
      if (!looks.length || !lookDialog) return;
      lookIndex = (index + looks.length) % looks.length;
      const button = looks[lookIndex], source = button.querySelector('img');
      const image = find('#pd-look-dialog-image');
      image.src = source.currentSrc || source.src;
      image.alt = source.alt;
      const shopLink = lookDialog.querySelector('.pd-look-shop');
      if (shopLink) {
        shopLink.hidden = !button.dataset.productUrl;
        if (button.dataset.productUrl) shopLink.href = button.dataset.productUrl;
        else shopLink.removeAttribute('href');
      }
      find('#pd-look-dialog-title').textContent = button.closest('.pd-look').querySelector('h3').textContent;
      find('#pd-look-counter').textContent = `${lookIndex + 1} / ${looks.length}`;
    }
    looks.forEach((button, i) => on(button, 'click', () => { showLook(i); openDialog(lookDialog); }));
    on(find('.pd-preview-prev'), 'click', () => showLook(lookIndex - 1));
    on(find('.pd-preview-next'), 'click', () => showLook(lookIndex + 1));
    on(lookDialog, 'keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); showLook(lookIndex + (event.key === 'ArrowRight' ? 1 : -1)); }
    });
    let touchStart = null;
    on(lookDialog, 'touchstart', event => { touchStart = event.touches[0].clientX; }, { passive: true });
    on(lookDialog, 'touchend', event => {
      if (touchStart === null) return;
      const distance = event.changedTouches[0].clientX - touchStart;
      if (Math.abs(distance) > 60) showLook(lookIndex + (distance < 0 ? 1 : -1));
      touchStart = null;
    }, { passive: true });
    const audio = find('#pd-soundtrack'), soundButton = find('.pd-sound-toggle');
    let soundWanted = false;
    function syncSound() {
      if (!audio || !soundButton) return;
      const playing = !audio.paused;
      soundButton.setAttribute('aria-pressed', String(playing));
      soundButton.setAttribute('aria-label', label(playing ? 'turn-sound-off' : 'turn-sound-on'));
      soundButton.querySelector('span').textContent = label(playing ? 'sound-on' : 'sound-off');
      soundButton.querySelector('img').src = icon(playing ? 'volume-2' : 'volume-x');
      root.classList.toggle('pd-sound-active', playing);
    }
    if (audio) audio.volume = 0.25;
    on(soundButton, 'click', async () => {
      if (!audio) return;
      soundWanted = !soundWanted;
      if (soundWanted) { try { await audio.play(); } catch { soundWanted = false; } }
      else audio.pause();
      syncSound();
    });
    on(audio, 'play', syncSound);
    on(audio, 'pause', syncSound);
    on(document, 'visibilitychange', () => {
      syncFilms();
      if (document.hidden) audio?.pause();
      else if (soundWanted) audio?.play().catch(() => { soundWanted = false; syncSound(); });
    });
    syncSound();
    root.dataset.initialized = 'true';
    cleanup = () => {
      controller.abort();
      filmObserver.disconnect(); sceneObserver.disconnect(); topObserver.disconnect(); mediaObserver.disconnect();
      if (heroPoster) heroPoster.onload = heroPoster.onerror = null;
      films.forEach(video => video.pause()); audio?.pause();
      root.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
      document.body.classList.remove('pd-locked');
      delete root.dataset.initialized;
    };
  }
  mount();
  let frame;
  ['shopify:section:load','shopify:section:unload'].forEach(type => document.addEventListener(type, event => {
    if (!event.target.classList.contains('pink-dusk-section')) return;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(mount);
  }));
})();
